/**
 * Socket.IO client singleton with auto-reconnect.
 * Pattern adapted from Refine's liveProvider abstraction:
 *   - subscribe(event, handler) returns unsubscribe fn
 *   - publish(event, payload) for future bi-directional use
 */
import { io, type Socket } from 'socket.io-client'
import { SOCKET_EVENTS } from '@monitor/shared'
import { config } from './env.js'

const TOKEN_STORAGE_KEY = 'admin_token'

/**
 * Read the admin JWT from localStorage (with sessionStorage fallback).
 *
 * Browsers DO NOT auto-attach httpOnly cookies to the Socket.IO
 * WebSocket handshake, and on mobile (iOS Safari, Android Chrome) the
 * httpOnly auth_token cookie is frequently stripped on cross-site
 * navigation or dropped on background-tab cleanup. The server-side
 * middleware accepts the JWT via `socket.handshake.auth.token` first,
 * then `Authorization: Bearer`, then the cookie — so we MUST pass the
 * token explicitly, otherwise mobile dashboards connect successfully
 * but immediately get `UNAUTHORIZED` from the server and never receive
 * `screen:new`.
 */
function readStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY) ?? sessionStorage.getItem(TOKEN_STORAGE_KEY)
  } catch {
    return null
  }
}

let socket: Socket | null = null

export function getSocket(): Socket {
  if (socket) return socket
  // Auth: pass JWT via `auth.token` so the server's Socket.IO middleware
  // (apps/server/src/plugins/socket-io.ts) can verify it on the polling
  // handshake, even when the httpOnly cookie is missing. `withCredentials:
  // true` is kept so the cookie path still works on desktop browsers.
  //
  // Transport: polling only.
  //   Cloudflare Pages Functions cannot proxy WebSocket upgrades (the request
  //   that hops from Cloudflare -> Fly is HTTP fetch() which doesn't carry a
  //   raw socket). On pages.dev the WS handshake is rejected with 426 by
  //   functions/socket.io/[[path]].ts, and socket.io-client keeps retrying
  //   that upgrade in the background — every retry spams DevTools with
  //   `WebSocket connection ... failed` and `lastError: Receiving end does
  //   not exist`. Worse, the failed upgrade can leave the polling engine
  //   permanently disconnected.
  //   Forcing `transports: ['polling']` and `upgrade: false` keeps the
  //   socket on long-poll only, which Pages Functions handle fine. The
  //   ~50ms extra latency per frame is invisible for a screenshot feed.
  //   On the Fly-direct deployment (custom domain behind Cloudflare CNAME,
  //   or vite dev) the WS path is still desired — flip `transports` to
  //   ['polling', 'websocket'] and `upgrade: true` if you point this at a
  //   host that forwards WS through.
  socket = io(config.SOCKET_URL, {
    autoConnect: true,
    transports: ['polling'],
    upgrade: false,
    withCredentials: true,
    auth: (cb) => cb({ token: readStoredToken() ?? '' }),
    reconnection: true,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 10_000,
    reconnectionAttempts: Infinity,
  })

  socket.on('connect_error', (err) => {
    // Server returns Error('UNAUTHORIZED') for missing/expired JWT. Bounce
    // to /login so the user can re-authenticate — otherwise we keep
    // reconnecting with the bad token every reconnectionDelay and tile
    // updates silently never arrive.
    const msg = String(err?.message ?? '')
    if (msg.includes('UNAUTHORIZED') && !window.location.pathname.startsWith('/login')) {
      try {
        localStorage.removeItem(TOKEN_STORAGE_KEY)
        sessionStorage.removeItem(TOKEN_STORAGE_KEY)
      } catch {
        /* storage disabled */
      }
      window.location.href = '/login'
      return
    }
    // eslint-disable-next-line no-console
    console.warn('socket connect_error:', msg)
  })
  return socket
}

export type Unsubscribe = () => void

/** Subscribe to a socket event. Returns an unsubscribe function. */
export function subscribe<T = unknown>(event: string, handler: (payload: T) => void): Unsubscribe {
  const s = getSocket()
  s.on(event, handler as (payload: unknown) => void)
  return () => s.off(event, handler as (payload: unknown) => void)
}

export function publish(event: string, payload?: unknown): void {
  getSocket().emit(event, payload)
}

export function joinEmployeeRoom(employeeId: number): void {
  publish(SOCKET_EVENTS.DASHBOARD_SUBSCRIBE_EMPLOYEE, employeeId)
}

export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect()
    socket = null
  }
}

export { SOCKET_EVENTS }
